import { Box } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';

/**
 * Shared, module-scope spacer primitives for FlatList/SectionList props
 * (`ItemSeparatorComponent`, `ListHeaderComponent`, `ListFooterComponent`,
 * etc.). An inline `() => <Box .../>` passed directly as one of these props
 * is a new component type on every render, so React unmounts and remounts
 * every header/footer/separator each pass instead of reusing it (SonarCloud
 * typescript:S6478, TA-10324). Import one of these instead of writing a new
 * inline spacer.
 *
 * Named by the single spacing prop + theme token they set, not by where
 * they're used — the same `MarginTopM` box works equally as a separator, a
 * header spacer, or a footer spacer.
 */
export const MarginTopXs: React.FC = () => <Box marginTop='xs' />;
export const MarginTopS: React.FC = () => <Box marginTop='s' />;
export const MarginTopM: React.FC = () => <Box marginTop='m' />;
export const MarginTopL: React.FC = () => <Box marginTop='l' />;

export const MarginBottomXs: React.FC = () => <Box marginBottom='xs' />;
export const MarginBottomS: React.FC = () => <Box marginBottom='s' />;
export const MarginBottomM: React.FC = () => <Box marginBottom='m' />;
export const MarginBottomL: React.FC = () => <Box marginBottom='l' />;
export const MarginBottomListFooter: React.FC = () => (
  <Box marginBottom='listFooter' />
);
export const PaddingBottomListFooter: React.FC = () => (
  <Box paddingBottom='listFooter' />
);

export const MarginLeftM: React.FC = () => <Box marginLeft='m' />;

export const MarginRightXxs: React.FC = () => <Box marginRight='xxs' />;
export const MarginRightXs: React.FC = () => <Box marginRight='xs' />;
export const MarginRightM: React.FC = () => <Box marginRight='m' />;

/** For a list prop that must be present but should render nothing. */
export const EmptySpacer: React.FC = () => null;

export const Divider: React.FC = () => (
  <Box height={1} backgroundColor='grey02' marginHorizontal='m' />
);
export const DividerMarginBottomListFooter: React.FC = () => (
  <Box
    height={1}
    backgroundColor='grey02'
    marginHorizontal='m'
    marginBottom='listFooter'
  />
);

/**
 * Bottom-of-list spacer that shows the load-more spinner while a next page is
 * being fetched. Pass it as an element (`ListFooterComponent={<ListLoadingFooter
 * isLoading={...} />}`) so the footer keeps one component type across renders.
 */
export const ListLoadingFooter: React.FC<{ isLoading: boolean }> = ({
  isLoading,
}) => (
  <Box marginBottom='listFooter'>
    {isLoading && <ActivityIndicatorLoading />}
  </Box>
);
